import { fromEvent, Subject, EMPTY } from 'rxjs';
import { tap, filter, map } from 'rxjs/operators';
import SearchBarResultCategory from 'enums/searchResultCategory';
import CompletionSuggestionType from 'enums/completionSuggestionType';
import SearchLocationType from 'enums/searchLocationType';
import SearchBarComponent from "./searchBarComponent";
import SearchBarResult from "./searchBarResult";
import SearchBarService from "../services/searchBarService";
import SearchBarHelper from "../utils/searchBarHelper";
import setHeaderAndSaveButtonVisibility from '../utils/uiVisibilityHandler';

/**
 * Class representing the Search Bar functionality.
 * Manages search input, results, and UI interactions.
 */
export default class SearchBar {
  constructor(config) {
    if (config) {
      this.subscriptions = [];
      this.searchResultsState = [];
      this.containerSearchTextMap = new Map();
      this.containerInstanceMap = new Map();
      this.searchBarResult = new SearchBarResult();
      this.selectedResult$ = new Subject();
      this.selectedResultData = null;

      this.searchContainers = config.searchContainers;
      this.smallScreenSearchContainer = config.smallScreenSearchContainer;
      this.searchBarModalOpen = config.searchBarModalOpen;
      this.searchBarModal = config.searchBarModal;
      this.header = config.header;
      this.savedSearchContainer = config.savedSearchContainer;
      this.activeSearchBarContainer = null;

      this.initializeState();
      this.applySideEffects();
    }
  }

  /**
   * Initializes the internal state of the SearchBar.
   */
  initializeState() {
    if (this.isSearchBarTextHidden()) {
      this.searchText = '';
    } else {
      this.lastSearchTextFromCookie = SearchBarHelper.trimText(SearchBarHelper.getLastSearchText());
      this.searchText = this.lastSearchTextFromCookie;
    }
  }

  /**
   * Applies initial side effects such as initializing the search bar,
   * binding small screen events, subscribing to component events,
   * and setting up click listeners for current location.
   */
  applySideEffects() {
    this.initializeSearchBar();
    this.bindSmallScreenEvents();
    this.subscribeToEvents();
  }

  /**
   * Formats the selected search result data into a displayable string.
   * @param {Object} selectedResultData - The selected search result data.
   * @returns {string} The formatted search text.
   */
  getFormattedSearchText() {
    const formattedText = SearchBarHelper.getFormattedSearchText(this.selectedResultData);
    return formattedText || '';
  }

  /**
   * Checks if the currently selected search result is a location type.
   * @returns {boolean} True if the selected result is a location, false otherwise.
   */
  isLocationSelected() {
    return `${this.selectedResultData?.categoryType}` === SearchBarResultCategory.Location.id;
  }

  /**
   * Determines whether the search bar text should be hidden.
   * We only suppress search bar text when we are doing a location based search.
   * @returns {boolean}
   */
  isSearchBarTextHidden() {
    return this.locationType === SearchLocationType.RectangularArea.name.toLowerCase() && !this.isLocationSelected();
  }

  /**
   * Determines the last search text to display in the search input.
   * Prefers the selected location's value if applicable.
   * @returns {string} The last search text.
   */
  getLastSearchText() {
    if (this.isSearchBarTextHidden()) {
      return '';
    }
    return this.isLocationSelected() ? this.getFormattedSearchText() || ''
      : SearchBarHelper.trimText(this.searchText || '');
  }

  /**
   * Builds the initial configuration for a SearchBarComponent.
   * @param {HTMLElement} searchBarContainer - The container element for the search bar.
   * @returns {Object} The configuration object for SearchBarComponent.
   */
  buildInitialSearchBarConfig(searchBarContainer) {
    const container = searchBarContainer;
    const searchInput = container.querySelector('[data-search-input]') || null;
    const resetButton = container.querySelector('[data-search-input-reset-button]') || null;
    const searchBarHighlight = container.querySelector('[data-search-bar-input-container]') || null;
    const currentLocation = container.querySelector('[data-current-location]') || null;

    return {
      container: searchBarContainer,
      searchInput,
      resetButton,
      searchBarHighlight,
      currentLocation,
      lastSearchText: this.getLastSearchText()
    };
  }

  /**
   * Destroys all active SearchBarComponent instances.
   */
  destroySearchBarComponents() {
    this.containerInstanceMap.forEach(component => {
      if (component) {
        component.destroy();
      }
    });

    this.containerInstanceMap.clear();
    this.containerSearchTextMap.clear();
  }

  /**
   * Initializes all search bar components on the page.
   * Creates a new SearchBarComponent for each container found.
   */
  initializeSearchBar() {
    this.destroySearchBarComponents();

    this.searchContainers.forEach(container => {
      const searchBarComponent = new SearchBarComponent();
      const config = this.buildInitialSearchBarConfig(container);
      searchBarComponent.init(config);

      this.containerInstanceMap.set(container, searchBarComponent);
      this.containerSearchTextMap.set(container, config.lastSearchText);
      this.bindCurrentLocationClickListener(config.currentLocation);
    });
  }

  /**
   * Binds click listeners for the current location element to manage visibility and modal behavior.
   * @param {HTMLElement} currentLocation - The current location element.
   */
  bindCurrentLocationClickListener(currentLocation) {
    if (!currentLocation) {
      return;
    }

    this.setCurrentLocationOutsideClickListener(currentLocation);
    this.setCurrentLocationClickListener(currentLocation);
  }

  /**
   * Binds events for small screen search bar modal open and close.
   */
  bindSmallScreenEvents() {
    if (this.searchBarModalOpen && this.searchBarModal) {
      const closeButton = this.smallScreenSearchContainer.querySelector('[data-close-search-bar-modal]');

      this.searchBarModalOpen.hidden = false;

      const openSubscription = fromEvent(this.searchBarModalOpen, 'click')
        .pipe(tap(() => this.handleSmallScreenSearchOpen()))
        .subscribe();

      const closeSubscription = fromEvent(closeButton, 'click')
        .pipe(tap(() => this.handleSmallScreenSearchClose()))
        .subscribe();

      this.subscriptions.push(openSubscription, closeSubscription);
    }
  }

  /**
   * Subscribes to input change and focus events from all SearchBarComponent instances.
   */
  subscribeToEvents() {
    this.containerInstanceMap.forEach(component => {
      const inputChangeSubscription = component.getInputChangeObserver()
        .pipe(
          filter(({ text }) => text !== this.lastSearchTextFromCookie),
          tap(({ container, text }) => {
            this.handleSearchTextChange(container, text);
          })
        )
        .subscribe();

      const inputFocusSubscription = component.getInputFocusObserver()
        .pipe(
          tap(event => this.handleInputFocus(event))
        )
        .subscribe();

      this.subscriptions.push(inputChangeSubscription, inputFocusSubscription);
    });

    this.searchResultsStateSubscription = SearchBarResult
      .getSelectedItemObservable(this.searchBarResult)
      .pipe(
        map((result) => {
          this.selectedResultData = result;
          return result;
        }),
        tap((result) => {
          if (
            result &&
            (
              result.categoryType === SearchBarResultCategory.Community.id ||
              result.categoryType === SearchBarResultCategory.Builder.id ||
              result.categoryType === SearchBarResultCategory.Article.id ||
              (result.categoryType === SearchBarResultCategory.Location.id &&
                result.completionSuggestionType === CompletionSuggestionType.State.name)
            )
          ) {
            document.body.dispatchEvent(new CustomEvent('search_result_non_srp_redirect', { detail: result }));
          }
          this.initializeSearchBar();
          this.handleSmallScreenSearchClose();
        })
      )
      .subscribe((result) => this.selectedResult$?.next(result));
    this.subscriptions.push(this.searchResultsStateSubscription);
  }

  /**
   * Provides an observable for page refresh or redirect events.
   * @param {Object} context - The context containing the observable.
   * @returns {Observable} The page refresh or redirect observable.
   */
  getSelectedResultObservable() {
    return this.selectedResult$ ?? EMPTY;
  }

  /**
   * Resets all search bars to their initial state.
   * @param {string} searchText - The current search text.
   * @param {HTMLElement} container - The container element of the search bar.
   */
  resetSearchBars(searchText, container) {
    const isEmptySearchText = SearchBarHelper.isNullOrWhiteSpace(searchText);
    if (isEmptySearchText) {
      const searchBarComponent = this.containerInstanceMap.get(container);
      if (searchBarComponent) {
        searchBarComponent.handleResetButtonClick();
      }
    }
  }

  /**
   * Handles changes to the search text input.
   * @param {HTMLElement} container - The container element of the search bar.
   * @param {string} searchText - The updated search text.
   */
  handleSearchTextChange(container, searchText) {
    this.containerSearchTextMap.set(container, searchText);
    this.resetSearchBars(searchText, container);
    this.toggleSearchBarUI(container);
    this.activeSearchBarContainer = container;
    this.searchTextChanged(searchText);
    this.processSearchResults(container, searchText);
  }

  /**
   * Processes the search text input by the user and fetches search results.
   * @param {string} searchText - The current search text input by the user.
   */
  async processSearchResults(container, searchText) {
    const trimmedSearchText = SearchBarHelper.trimText(searchText);

    if (!trimmedSearchText) {
      this.handleNoSearchText(container);
      return;
    }

    try {
      const searchResults = await SearchBarService.fetchSearchResults(trimmedSearchText);
      const results = SearchBarHelper.extractResults(searchResults);

      this.updateSearchState(results);
      this.displaySearchResults(container, trimmedSearchText, results.length ? results : this.searchResultsState);
    } catch {
      this.displaySearchResults(container, trimmedSearchText, this.searchResultsState);
    }
  }

  /**
   * Handles the scenario when there is no search text input.
   * Displays an empty search results state.
   */
  handleNoSearchText(container) {
    this.displaySearchResults(container, '', []);
  }

  /**
   * Updates the internal search results state if new results are available.
   * @param {Array} results - The new search results to update the state with.
   */
  updateSearchState(results) {
    if (results.length) {
      this.searchResultsState = results;
    }
  }

  /**
   * Displays search results in the search bar results container.
   * @param {string} searchText - The current search text input by the user.
   * @param {Array} searchResults - The array of search results to display.
   */
  displaySearchResults(container, searchText, searchResults = []) {
    if (container) {
      const resultsContainer = container.querySelector('[data-search-bar-result-container]');
      this.resetSearchResult();
      this.initializeSearchResult(resultsContainer, searchText, searchResults);
    }
  }

  /**
   * Resets the search results by destroying the existing SearchBarResult instance.
   */
  resetSearchResult() {
    if (this.searchBarResult) {
      this.searchBarResult.destroy();
    }
  }

  /**
   * Initializes the search results display with the provided container, search text, and results.
   * @param {HTMLElement} container - The container element for displaying search results.
   * @param {string} searchText - The current search text input by the user.
   * @param {Array} searchResults - The array of search results to display.
   */
  initializeSearchResult(container, searchText, searchResults) {
    this.searchBarResult.initialize({
      resultContainer: container,
      searchText,
      searchResults
    });
  }

  /**
   * Handles changes to the search text input.
   * @param {string} searchText - The updated search text.
   */
  searchTextChanged(searchText) {
    this.searchText = searchText;
  }

  /**
   * Handles the focus event on the search input to manage UI elements.
   * @param {Event} event - The focus event object.
   */
  handleInputFocus(event) {
    const container = event.target.closest('[data-search-bar-container]');
    this.activeSearchBarContainer = container;
    if (container) {
      this.toggleSearchBarUI(container);
    }
  }

  /**
   * Toggles the search bar UI elements based on the current search text.
   * @param {HTMLElement} container - The container element of the search bar.
   */
  toggleSearchBarUI(container) {
    this.toggleCurrentLocation(container);
    this.updateSmallScreenMessageVisibility(false);
  }

  /**
   * Toggles the visibility of the current location element based on search text.
   * @param {HTMLElement} container - The container element containing the current location.
   * @param {boolean} [shouldForceHide=false] - Whether to force hide the current location.
   */
  toggleCurrentLocation(container, shouldForceHide = false) {
    const searchText = this.containerSearchTextMap.get(container) || '';
    const hide = SearchBarHelper.shouldHideCurrentLocation(searchText, shouldForceHide);
    SearchBar.applyCurrentLocationVisibility(container, hide);
  }

  /**
   * Applies visibility to the current location element within the specified container.
   * @param {HTMLElement} container - The container element containing the current location.
   * @param {boolean} hidden - Whether to hide the current location element.
   */
  static applyCurrentLocationVisibility(container, hidden) {
    const currentLocation = container.querySelector('[data-current-location]') || null;
    if (currentLocation) {
      currentLocation.hidden = hidden;
    }
  }

  /**
   * Handles the opening of the small screen search bar modal.
   */
  handleSmallScreenSearchOpen() {
    setHeaderAndSaveButtonVisibility(true, this.header, this.savedSearchContainer);
    this.updateSmallScreenMessageVisibility();
    this.toggleSmallSearchBarVisibility(true);
    this.activeSearchBarContainer = this.smallScreenSearchContainer;
    
    const container = this.activeSearchBarContainer.closest("[data-typeahead-container]");
    container?.classList.add('nhs-u-display--flex');

    SearchBarHelper.toggleScroll(document.body, true);
  }

  /**
   * Handles the closing of the small screen search bar modal.
   */
  handleSmallScreenSearchClose() {
    setHeaderAndSaveButtonVisibility(false, this.header, this.savedSearchContainer);
    this.toggleSmallSearchBarVisibility(false);

    const container = this.activeSearchBarContainer.closest("[data-typeahead-container]");
    container?.classList.remove('nhs-u-display--flex');

    SearchBarHelper.toggleScroll(document.body, false);
  }

  /**
   * Updates the visibility of the small screen search results message container.
   */
  updateSmallScreenMessageVisibility(isVisible = true) {
    if (!this.smallScreenSearchContainer) {
      return;
    }

    const container = this.smallScreenSearchContainer?.querySelector('[data-search-results-info]');
    if (container) {
      const searchKeywordContainer = container?.querySelector('[data-search-keyword-container]');
      const shouldDisplaySearchInstructions = isVisible && this.shouldDisplaySearchInstructions();

      SearchBar.applyContainerVisibility(container, shouldDisplaySearchInstructions);
      SearchBar.applyContainerVisibility(searchKeywordContainer, false);
    }
  }

  /**
   * Applies visibility to a given container element.
   * @param {HTMLElement} container - The container element to update visibility for.
   * @param {boolean} isVisible - Whether the container should be visible.
   */
  static applyContainerVisibility(container, isVisible) {
    if (container) {
      container.hidden = !isVisible;
    }
  }

  /**
   * Determines whether to display search instructions based on the current search text.
   * @returns {boolean} True if the search instructions should be displayed, false otherwise.
   */
  shouldDisplaySearchInstructions() {
    const searchText = this.containerSearchTextMap.get(this.smallScreenSearchContainer) || '';
    return SearchBarHelper.isNullOrWhiteSpace(searchText);
  }

  /**
   * Determines whether to show the small screen search bar modal.
   * @param {boolean} show - True to show the modal, false to hide it.
   * @returns {boolean} True if the modal should be shown, false otherwise.
   */
  shouldShowSmallSearchBar(show) {
    return this.smallScreenSearchContainer && show;
  }

  /**
   * Toggles the visibility of the small screen search bar modal.
   * @param {boolean} show - True to show the modal, false to hide it.
   */
  toggleSmallSearchBarVisibility(show) {
    const visible = this.shouldShowSmallSearchBar(show);
    this.updateSmallSearchBarVisibility(visible);
  }

  /**
   * Updates the visibility of the small screen search bar modal.
   * @param {boolean} show - True to show the modal, false to hide it.
   */
  updateSmallSearchBarVisibility(show) {
    if (this.searchBarModal) {
      this.searchBarModal.hidden = !show;
    }
  }

  /**
   * Sets up a document-wide click listener to manage current location visibility.
   */
  setCurrentLocationOutsideClickListener(currentLocation) {
    if (!currentLocation || !this.smallScreenSearchContainer) { return; }

    const clickSubscription = fromEvent(document, 'click')
      .pipe(
        tap(event => this.handleDocumentClick(event, currentLocation))
      )
      .subscribe();

    this.subscriptions.push(clickSubscription);
  }

  /**
   * Checks if a click event target is inside relevant elements (current location or search bar input).
   * @param {HTMLElement} target - The target element of the click event.
   * @returns {boolean} - True if the click is inside relevant elements, false otherwise.
   */
  isClickInsideRelevantElements(target, currentLocation) {
    const isInsideCurrentLocation = currentLocation?.contains(target);
    const searchBarInputContainer = this.activeSearchBarContainer?.querySelector('[data-search-bar-input-container]');
    const isInsideSearchBarInput = searchBarInputContainer?.contains(target);

    return isInsideCurrentLocation || isInsideSearchBarInput;
  }

  /**
   * Handles document click events to manage current location visibility and search results message.
   * @param {Event} event - The click event object.
   */
  handleDocumentClick(event, currentLocation) {
    const clickedInside = this.isClickInsideRelevantElements(event.target, currentLocation);

    if (!clickedInside) {
      if (this.activeSearchBarContainer) {
        this.hideCurrentLocation(this.activeSearchBarContainer);
      }
    }

    this.toggleSearchResultsMessageContainer();
  }

  /**
   * Hides the current location element.
   */
  hideCurrentLocation(container) {
    this.toggleCurrentLocation(container, true);
  }

  /**
   * Toggles the visibility of the search results message container in small screen mode.
   */
  toggleSearchResultsMessageContainer() {
    if (!this.smallScreenSearchContainer) {
      return;
    }

    const container = this.smallScreenSearchContainer.querySelector('[data-search-results-info]');
    const searchKeywordContainer = container?.querySelector('[data-search-keyword-container]');
    const isHiddenCurrentLocation = this.smallScreenSearchContainer.querySelector('[data-current-location]')?.hidden;
    const shouldDisplaySearchInstructions = this.shouldDisplaySearchInstructions() && isHiddenCurrentLocation;

    SearchBar.applyContainerVisibility(container, shouldDisplaySearchInstructions);
    SearchBar.applyContainerVisibility(searchKeywordContainer, false);
  }

  /**
   * Sets up a click listener for the current location element to close the small screen search.
   */
  setCurrentLocationClickListener(currentLocation) {
    if (!currentLocation) {
      return;
    }

    const clickSubscription = fromEvent(currentLocation, 'click')
      .pipe(
        tap(() => this.handleSmallScreenSearchClose())
      )
      .subscribe();

    this.subscriptions.push(clickSubscription);
  }

  /**
   * Cleans up all subscriptions and destroys search bar components.
   */
  destroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.containerInstanceMap.forEach(component => component.destroy());
    this.resetSearchResult();
  }
}
